export default class FormsHandler {
    constructor() {
        this.formInstances = [];
    }

    init() {
        this.initAllForms();
        this.initVacancyForm();
    }

    initAllForms() {
        // Find all iframes with data-form attribute
        const formIframes = document.querySelectorAll('[data-form]');

        if (!formIframes.length) return;

        formIframes.forEach((iframe) => {
            this.initSingleForm(iframe);
        });
    }

    initVacancyForm() {
        const vacancyForm = document.getElementById('vacancies-form');
        if (!vacancyForm) return;

        // Handle CV upload file name display
        const cvUploadInput = document.getElementById('cv-upload');
        const cvUploadLabel = cvUploadInput?.closest('label')?.querySelector('span:first-child');

        if (cvUploadInput && cvUploadLabel) {
            cvUploadInput.addEventListener('change', (e) => {
                const file = e.target.files[0];
                if (file) {
                    cvUploadLabel.textContent = file.name;
                } else {
                    cvUploadLabel.textContent = 'Attach file (CV)';
                }
            });
        }

        vacancyForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            const submitButton = vacancyForm.querySelector('button[type="submit"]');
            const formData = new FormData(vacancyForm);
            const formAction = vacancyForm.getAttribute('action');

            // Disable submit button
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.classList.add('opacity-50', 'cursor-not-allowed');
            }

            const showMessage = (className, message) => {
                // Get or create message container
                let messageContainer = document.getElementById('form-message');
                if (!messageContainer) {
                    messageContainer = document.createElement('div');
                    messageContainer.id = 'form-message';
                    messageContainer.setAttribute('role', 'alert');
                    vacancyForm.parentNode.insertBefore(messageContainer, vacancyForm);
                }
                messageContainer.className = className;
                messageContainer.textContent = message;
            };

            try {
                if (typeof grecaptcha !== 'undefined') {
                    const siteKey = submitButton.getAttribute('data-sitekey');
                    const token = await grecaptcha.execute(siteKey, { action: 'submit' });
                    formData.append('g-recaptcha-response', token);
                }

                const response = await fetch(formAction, {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: formData
                });

                const responseText = await response.text();
                let result;

                try {
                    result = JSON.parse(responseText);
                } catch (parseError) {
                    throw new Error('Server returned invalid JSON: ' + responseText.substring(0, 200));
                }

                if (result.success) {
                    showMessage(
                        'p-sm rounded-lg bg-green-100 text-green-800 border border-green-200',
                        result.data.message || 'Thank you for your application!'
                    );
                    vacancyForm.reset();
                    // Reset CV upload label text
                    if (cvUploadLabel) {
                        cvUploadLabel.textContent = 'Attach file (CV)';
                    }
                } else {
                    showMessage(
                        'p-sm rounded-lg bg-red-100 text-red-800 border border-red-200',
                        result.data.message || 'Something went wrong. Please try again.'
                    );
                }
            } catch (error) {
                showMessage(
                    'p-sm rounded-lg bg-red-100 text-red-800 border border-red-200',
                    'An error occurred. Please try again.'
                );
            } finally {
                // Re-enable submit button
                if (submitButton) {
                    submitButton.disabled = false;
                    submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
                }
            }
        });
    }

    initSingleForm(iframe) {
        const formType = iframe.getAttribute('data-form');
        const formInstance = {
            iframe,
            formType,
            observers: [],
            intervals: []
        };

        // Define fallback heights for different form types
        const fallbackHeights = {
            'contact-form': '800px',
            'gated': '600px',
            'schedule-meeting': '600px'
        };

        const fallbackHeight = fallbackHeights[formType] || '800px';

        // Handle iframe auto-resize
        const loadHandler = () => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDoc) {
                    const resizeIframe = () => {
                        const height = iframeDoc.body.scrollHeight;
                        iframe.style.height = height + 'px';
                    };

                    resizeIframe();

                    // Watch for changes in iframe content
                    const observer = new MutationObserver(resizeIframe);
                    observer.observe(iframeDoc.body, {
                        childList: true,
                        subtree: true,
                        attributes: true
                    });

                    formInstance.observers.push(observer);
                }
            } catch (e) {
                // Cross-domain iframe - auto-resize not available
                // Fallback: set a reasonable height based on form type
                iframe.style.height = fallbackHeight;
            }
        };

        iframe.addEventListener('load', loadHandler);

        // Periodic height check (fallback)
        let attempts = 0;
        const heightCheck = setInterval(() => {
            attempts++;
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDoc && iframeDoc.body) {
                    const height = iframeDoc.body.scrollHeight;
                    if (height > 100) { // Valid content loaded
                        iframe.style.height = height + 'px';
                        clearInterval(heightCheck);
                    }
                }
            } catch (e) {
                // Cross-domain, stop trying after 10 attempts
                if (attempts > 10) {
                    clearInterval(heightCheck);
                }
            }
        }, 500);

        formInstance.intervals.push(heightCheck);
        this.formInstances.push(formInstance);
    }

    destroy() {
        // Clean up all form instances
        this.formInstances.forEach(instance => {
            // Clear all intervals
            instance.intervals.forEach(interval => clearInterval(interval));

            // Disconnect all observers
            instance.observers.forEach(observer => observer.disconnect());
        });

        // Clear message handler
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }

        this.formInstances = [];
    }
}

// Global message handler for HubSpot forms (shared across all instances)
if (typeof window !== 'undefined') {
    window.addEventListener('message', (event) => {
        // Filter out React DevTools spam
        if (event.data && event.data.source === 'react-devtools-content-script') {
            return;
        }

        // Handle iframe height updates from HubSpot
        if (event.origin === 'https://share-eu1.hsforms.com' && event.data.height) {
            // Find the target iframe by matching the event source
            const iframes = document.querySelectorAll('[data-form]');
            iframes.forEach(iframe => {
                try {
                    if (iframe.contentWindow === event.source) {
                        iframe.style.height = event.data.height + 'px';
                    }
                } catch (e) {
                    // Cross-origin check failed, skip
                }
            });
        }
    });
}